import React from 'react'
import classnames from 'classnames'

import styles from './Text.module.scss'

const TEXT_ALIGNMENTS = ['left', 'right', 'center'] as const
type TextAlignments = typeof TEXT_ALIGNMENTS[number]

const TEXT_SIZES = ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl'] as const
type TextSizes = typeof TEXT_SIZES[number]

const TEXT_WEIGHTS = ['light', 'regular', 'semibold', 'bold'] as const
type TextWeights = typeof TEXT_WEIGHTS[number]

const TEXT_COLORS = [
  'green',
  'orange',
  'red',
  'blue',
  'pink',
  'heather',
  'white',
  'inherit',
  'gray',
  'eggShell',
  'twilight',
] as const
export type TextColors = typeof TEXT_COLORS[number]

const TEXT_TRANSFORMS = ['capitalize', 'lowercase', 'uppercase'] as const
type TextTransforms = typeof TEXT_TRANSFORMS[number]

const TEXT_LETTER_SPACING = ['normal', 'large'] as const
type TextLetterSpacing = typeof TEXT_LETTER_SPACING[number]

interface TextProps {
  align?: TextAlignments // add text-alignment
  element?: keyof JSX.IntrinsicElements // render something other than a 'p' tag, such as an h1
  ellipsis?: boolean // truncate single-line overflowing text with ellipsis
  size?: TextSizes // modify the font size
  weight?: TextWeights // modify the font weight
  color?: TextColors // modify the color
  nowrap?: boolean
  transform?: TextTransforms
  paragraph?: boolean
  upperspaced?: boolean
  className?: string
  letterSpacing?: TextLetterSpacing
  children?: React.ReactNode
}

const Text = ({
  align,
  className,
  color = 'gray',
  element: Element = 'p',
  ellipsis = false,
  paragraph = false,
  size = 'm',
  nowrap,
  transform,
  weight = 'regular',
  upperspaced = false,
  letterSpacing = 'normal',
  ...restProps
}: TextProps) => {
  const textClassName = classnames(
    className,
    styles.text,
    ellipsis && styles.ellipsis,
    styles[`size-${size}`],
    styles[`weight-${weight}`],
    styles[`color-${color}`],
    styles[`align-${align}`],
    styles[`transform-${transform}`],
    styles[`letter-spacing-${letterSpacing}`],
    nowrap && styles.nowrap,
    paragraph && styles.paragraph,
    upperspaced && styles.upperspaced
  )

  return <Element className={textClassName} {...restProps} />
}

Text.Colors = TEXT_COLORS
Text.Alignments = TEXT_ALIGNMENTS
Text.Sizes = TEXT_SIZES
Text.Weights = TEXT_WEIGHTS
Text.Transforms = TEXT_TRANSFORMS
Text.LetterSpacing = TEXT_LETTER_SPACING

export default Text
